/* Code Legend DTO
    public class CodeLegendDTO
    {
        public int CodeLegendId { get; set; } (Primary key, not editable)
        public string Title { get; set; }
        public int TenantId { get; set; } (Not editable)
        public int? LegendContextId { get; set; } (Not editable)
        public bool IsShareable { get; set; }
        public List<CodeLegendEntryDTO> Entries { get; set; }
    }
*/

/* Code Legend Entry DTO
    public class CodeLegendEntryDTO
    {
        public int CodeLegendEntryId { get; set; } (Primary key, not editable)
        public int CodeLegendId { get; set; } (Parent foreign key, not editable)
        public int? CodeLegendMasterEntryId { get; set; } (Foreign key to master entry, not editable)
        public int Code { get; set; }
        public string? Description { get; set; }
        public string? NonConformingCode { get; set; } (Not editable)
        public int? AlarmStateId { get; set; }
        public bool? SendAlerts { get; set; }
        public int? ComponentId { get; set; } (Not editable)
        public int? SystemId { get; set; } (Not editable)
        public int? IsMechId { get; set; }
    }
*/

/* Code Legend Master Entry DTO
    public class CodeLegendMasterEntryDTO
    {
        public int CodeLegendMasterEntryId { get; set; } (Primary key, not editable)
        public string Description { get; set; }
        public int AlarmStateId { get; set; }
        public int IsMechId { get; set; }
        public bool SendAlerts { get; set; }
        public List<CodeLegendMasterEntryAltDescDTO> AltDescriptions { get; set; }
    }
*/

/* Code Legend Master Entry Alt Desc DTO
    public class CodeLegendMasterEntryAltDescDTO
    {
        public int CodeLegendMasterEntryAltDescId { get; set; } (Primary key, not editable)
        public int CodeLegendMasterEntryId { get; set; } (Not editable)
        public string Description { get; set; }
    }
*/

import { useContext, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Typography,
  Box,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CodeLegendEditor from "./CodeLegendEditor";
import { CodeLegendContext } from "../../common/contexts/CodeLegendContext";
import { TenantContext } from "../../common/contexts/TenantContext";
import { deleteCodeLegend } from "../../api/APIHelpers";
import { AlertsContext } from "../../common/contexts/AlertsContext";
import { extractApiErrorMessage } from "../../common/helpers/ErrorUtils";

const CodeLegendsConfigurator = () => {
  const { codeLegends, refreshCodeLegendContext } =
    useContext(CodeLegendContext);
  const { setInfoMessages } = useContext(AlertsContext);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedLegendId, setSelectedLegendId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const tenantInfo = useContext(TenantContext);

  const blankCodeLegend = {
    CodeLegendId: 0,
    Title: "",
    TenantId: tenantInfo.currentTenantId,
    LegendContextId: null,
    IsShareable: false,
    Entries: [],
  };

  // Check if codeLegends is loaded and is an array
  const isLoading = !Array.isArray(codeLegends);
  const filteredLegends = !isLoading
    ? codeLegends.filter((legend) =>
        legend.label.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  // Pagination logic
  const paginatedLegends = filteredLegends.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenEditor = (id) => {
    setSelectedLegendId(id);
    setEditorOpen(true);
  };

  const handleCloseEditor = () => {
    setEditorOpen(false);
    setSelectedLegendId(null);
  };

  const handleOpenDeleteDialog = (id) => {
    setDeleteTargetId(id);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setDeleteTargetId(null);
  };

  const handleDeleteLegend = async () => {
    setDeleting(true);
    try {
      await deleteCodeLegend(deleteTargetId);
      setInfoMessages({
        type: "snackbar",
        message: `Code Legend deleted successfully.`,
        severity: "success",
        open: true,
      });
      await refreshCodeLegendContext();
    } catch (err) {
      const errorMessage = extractApiErrorMessage(err);
      setInfoMessages({
        type: "snackbar",
        message: `Error deleting Code Legend: ${errorMessage}`,
        severity: "error",
        open: true,
      });
    } finally {
      setDeleting(false);
      handleCloseDeleteDialog();
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h5' gutterBottom>
        Code Legends
      </Typography>
      <TextField
        label='Search by Title'
        variant='outlined'
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          if (page !== 0) {
            handleChangePage(null, 0);
          }
        }}
        sx={{ mb: 2, width: 300 }}
        disabled={isLoading}
      />
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={2} align='center'>
                  Loading...
                </TableCell>
              </TableRow>
            ) : paginatedLegends.length > 0 ? (
              paginatedLegends.map((legend) => (
                <TableRow key={legend.id}>
                  <TableCell>{legend.label}</TableCell>
                  <TableCell>
                    <IconButton
                      aria-label='edit'
                      color='primary'
                      onClick={() => handleOpenEditor(legend.id)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      aria-label='delete'
                      color='error'
                      onClick={() => handleOpenDeleteDialog(legend.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align='center'>
                  No Code Legends found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component='div'
          count={filteredLegends.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage='Rows per page:'
        />
      </TableContainer>

      <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
        <Button
          variant='contained'
          color='primary'
          onClick={() => {
            setSelectedLegendId("new");
            setEditorOpen(true);
          }}>
          Add New Code Legend
        </Button>
      </Box>

      <Dialog
        open={editorOpen}
        onClose={handleCloseEditor}
        maxWidth='lg'
        fullWidth>
        <DialogTitle>Code Legend Editor</DialogTitle>
        <DialogContent>
          {selectedLegendId === "new" ? (
            <CodeLegendEditor
              codeLegendId={null}
              initialLegend={blankCodeLegend}
            />
          ) : (
            selectedLegendId && (
              <CodeLegendEditor codeLegendId={selectedLegendId} />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditor}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Delete Code Legend</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this Code Legend?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteLegend}
            color='error'
            disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CodeLegendsConfigurator;
